import {
  AdminSpinner,
  ChatEmptyState,
  ChatPlusIcon,
  ChatSearchIcon,
  ConversationListItemResponse,
  H3,
  lightTheme,
  ListSpinner,
  SearchBar,
  SearchField,
  useChatUnreadCount,
} from '@vgrubs/components';
import { ChatFilters } from './ChatFilters';
import { ChatInboxRow } from './ChatInboxRow';
import { ViktorEntryCard } from './ViktorEntryCard';
import {
  InboxColumn,
  InboxControls,
  InboxEmpty,
  InboxList,
  InboxPanel,
  InboxTitle,
  NewButton,
  ScopeTab,
  ScopeTabCount,
  ScopeTabs,
  SearchSlot,
} from './Chat.styled';
import { UseChatInbox } from './hooks/useChatInbox';
import { useChatFilters } from './hooks/useChatFilters';
import { PUBLIC_CHAT_MOCK } from './publicChatMock';

type Props = {
  /** Everything the page's scope, search and selection state exposes. */
  controls: ReturnType<typeof useChatFilters>;
  inbox: UseChatInbox;
  /** The scroll container, so the paging observer can root on it. */
  listRef: React.MutableRefObject<HTMLDivElement | null>;
  onNewConversation: () => void;
  /** False when the current tab has no conversation the viewer may start. */
  canCreate: boolean;
  /** Below 1200px only one pane is mounted; the open thread replaces this. */
  hidden: boolean;
  /**
   * Whether the Viktor card is drawn at all - admin and support roles only,
   * whatever their permissions. The backend answers 403 to anyone else.
   */
  canUseViktor: boolean;
  /** The viewer's one Viktor thread; undefined until its POST answers. */
  viktorThread?: ConversationListItemResponse;
};

/**
 * The left pane: the two halves of the inbox, the search and the list.
 * Nothing here reacts to the socket - the list brings itself up to date, see
 * `useChatInbox`.
 */
export const ChatInboxPanel = ({
  controls,
  inbox,
  listRef,
  onNewConversation,
  canCreate,
  hidden,
  canUseViktor,
  viktorThread,
}: Props) => {
  const { filters, selectedId, selectThread } = controls;
  const { threads, isLoading, isFetchingNextPage } = inbox;

  /*
   * The same query the sidebar badge runs, so this costs no request of its own
   * and the socket keeps both current. Only the per-half split is read here -
   * the total is already on the sidebar, two feet to the left.
   */
  const { count: unread } = useChatUnreadCount();

  /** The Internal tab answers for Viktor too - see the tab itself. */
  const internalUnread =
    (unread.internal?.messages ?? 0) + (unread.viktor?.messages ?? 0);

  /* And Merchants for the merchant groups, which it lists beside the support
     threads while the backend counts them in a bucket of their own. */
  const merchantUnread =
    (unread.merchant?.messages ?? 0) + (unread.merchantGroups?.messages ?? 0);

  /*
   * The Dispatcher tab, once there is a backend that knows it: it says so by
   * counting it. Until then only the local mock draws it.
   */
  const hasPublicChats = PUBLIC_CHAT_MOCK || !!unread.dispatcher;
  const publicScope = filters.scope === 'dispatcher';

  /** Threads with something unread on the tab on screen, for the chip. */
  const unreadGroups = unread.merchantGroups?.threads ?? 0;
  const unreadThreads =
    filters.scope === 'merchant'
      ? filters.groupsOnly
        ? unreadGroups
        : (unread.merchant?.threads ?? 0) + unreadGroups
      : unread[filters.scope]?.threads;

  /** Past 99 the number stops being a number and becomes a shape. */
  const label = (value?: number) =>
    !value ? null : value > 99 ? '99+' : String(value);

  const empty = !isLoading && threads.length === 0 && !isFetchingNextPage;

  return (
    <InboxColumn $hiddenOnMobile={hidden}>
      {/* The list, not the panel, is the scroll container - so the paging
          observer roots on this element, and the search above it stays put
          while the rows move. */}
      <InboxPanel>
        {/* The coarsest cut in the page, so it sits above everything the list
            is searched and filtered with - and inside the panel with them,
            rather than floating above it, so the whole inbox reads as one
            object. */}
        <ScopeTabs $wrap={hasPublicChats}>
          <ScopeTab
            type="button"
            $selected={filters.scope === 'merchant'}
            onClick={() => controls.changeScope('merchant')}
          >
            Merchants
            {!!label(merchantUnread) && (
              <ScopeTabCount>{label(merchantUnread)}</ScopeTabCount>
            )}
          </ScopeTab>
          {/* Between the two, because that is what it is: the same support
              queue, answered by the same people, with an agent on the other
              end instead of a merchant. */}
          <ScopeTab
            type="button"
            $selected={filters.scope === 'sales'}
            onClick={() => controls.changeScope('sales')}
          >
            Sales
            {!!label(unread.sales?.messages) && (
              <ScopeTabCount>{label(unread.sales?.messages)}</ScopeTabCount>
            )}
          </ScopeTab>
          <ScopeTab
            type="button"
            $selected={filters.scope === 'internal'}
            onClick={() => controls.changeScope('internal')}
          >
            Internal Team
            {/* Viktor's unread rides on this tab, because his card is inside
                it: the backend keeps the `viktor` bucket out of `internal` -
                his thread is not a conversation with a person - so the two are
                added up here rather than there. Without it an answer that
                arrived while another tab was open would be a card nobody can
                see, under a tab with nothing on it. */}
            {!!label(internalUnread) && (
              <ScopeTabCount>{label(internalUnread)}</ScopeTabCount>
            )}
          </ScopeTab>
          {/* After the team's own conversations: these are strangers -
              customers waiting on a vDrive delivery - and an AI answers them
              before anyone here does. */}
          {hasPublicChats && (
            <>
              <ScopeTab
                type="button"
                $selected={filters.scope === 'dispatcher'}
                onClick={() => controls.changeScope('dispatcher')}
              >
                Dispatcher
                {!!label(unread.dispatcher?.messages) && (
                  <ScopeTabCount>
                    {label(unread.dispatcher?.messages)}
                  </ScopeTabCount>
                )}
              </ScopeTab>
              {/* The Website tab is off with the website chat for now
                  (docs/dispatcher-chat-front.md). It was a ScopeTab for
                  scope 'website' with the unread.website count. */}
            </>
          )}
        </ScopeTabs>

        <InboxControls>
          <SearchSlot>
            {/* Built from the composer's own styles rather than AdminInput, so
                the two fields in this card are the same field - same surface,
                type size and placeholder - with a magnifier where the composer
                keeps its icons. */}
            <SearchBar>
              <SearchField
                value={filters.query}
                placeholder="Search Conversations"
                onChange={(event) => controls.changeQuery(event.target.value)}
              />
              <ChatSearchIcon />
            </SearchBar>
          </SearchSlot>
        </InboxControls>

        <ChatFilters
          unansweredOnly={filters.unansweredOnly}
          onUnansweredOnlyChange={controls.setUnansweredOnly}
          unreadOnly={filters.unreadOnly}
          onUnreadOnlyChange={controls.setUnreadOnly}
          archived={filters.archived}
          onArchivedChange={controls.setArchived}
          /* Both support tabs have somebody waiting on an answer; a group
             never does. */
          showUnanswered={filters.scope !== 'internal'}
          unansweredLabel={publicScope ? 'Needs a person' : undefined}
          unreadThreads={unreadThreads}
          groupsOnly={filters.groupsOnly}
          onGroupsOnlyChange={controls.setGroupsOnly}
          showGroups={filters.scope === 'merchant'}
          unreadGroups={unreadGroups}
        />

        {/* Above the title because Viktor is not IN the list: the heading
            names the conversations, and his thread neither scrolls nor filters
            with them.

            Internal Team only. The two support tabs are queues of people
            waiting to be answered, and a private thread with an AI pinned over
            one of them reads as one of those; Internal is where the viewer's
            own conversations live, which is what this is. The tab's count
            carries him while it is closed - see above. */}
        {canUseViktor && filters.scope === 'internal' && (
          <ViktorEntryCard
            thread={viktorThread}
            selected={!!viktorThread && viktorThread.id === selectedId}
            onClick={() => viktorThread && selectThread(viktorThread.id)}
          />
        )}

        {/* Names the list rather than the screen, which is why it sits down
            here: everything above it - the two halves, the search, the chips -
            decides WHICH messages, and this is where they start.

            H3, not H4: that one is the app's uppercase, letter-spaced label
            style, and "M E S S A G E S" reads as a section marker rather than
            as a title. */}
        <InboxTitle>
          <H3 $mb="0">Messages</H3>
        </InboxTitle>

        <InboxList ref={listRef}>
          {threads.map((thread: ConversationListItemResponse) => (
            <ChatInboxRow
              key={thread.id}
              thread={thread}
              selected={thread.id === selectedId}
              onClick={() => selectThread(thread.id)}
            />
          ))}

          {empty && (
            <InboxEmpty>
              {/* One line, not two: the "No Conversations" headline said
                  nothing the list's own emptiness does not already say. */}
              <ChatEmptyState
                text={
                  filters.query
                    ? 'No conversations match your search.'
                    : filters.archived
                    ? 'Nothing archived.'
                    : filters.groupsOnly
                    ? 'No group chats yet.'
                    : filters.scope === 'dispatcher'
                    ? 'No customer has written from a tracking link yet.'
                    : 'No conversations yet.'
                }
              />
            </InboxEmpty>
          )}

          {/* Collapsed while idle: `ListSpinner` reserves 70px, which in a
              bounded column just reads as dead space under the last row once
              you scroll to the end. The sentinel keeps a 1px box so the
              observer still has something to watch. */}
          <ListSpinner
            loading={isFetchingNextPage}
            observerRef={inbox.observerRef}
            height={isFetchingNextPage ? '70px' : '1px'}
            heightSM={isFetchingNextPage ? '70px' : '1px'}
            mb={isFetchingNextPage ? '-40' : '0'}
          />
          {/* Small, like the thread's: at full size it fills a 300px column
              corner to corner. */}
          {isLoading && <AdminSpinner absolute small $noBackground />}
        </InboxList>

        {/* Floats over the rows in the corner rather than sitting in the row of
            controls: starting a conversation is the one thing on this screen
            that is not about the list you are looking at, and in that row it
            read as a third way to narrow it.

            Absent rather than disabled where there is nothing it could make:
            support threads are the one kind that needs a permission, and on
            that tab without it the button opens a modal with no options. */}
        {canCreate && (
          <NewButton
            type="button"
            aria-label="New conversation"
            onClick={onNewConversation}
          >
            <ChatPlusIcon color={lightTheme.white} />
          </NewButton>
        )}
      </InboxPanel>
    </InboxColumn>
  );
};
